import axios from 'axios';
import { createElement } from 'react';
import { supabase } from './supabaseClient';
import { logAPIError, logNetworkError, addBreadcrumb } from './errorLogger';
import { isAnonAccessibleRoute } from '@/application/auth/routePolicy';
import { baglantiYenidenDeneKur } from './baglantiYenidenDene';

// Backend API URL'ini ortam değişkeninden al veya varsayılan olarak localhost:3001'i kullan
const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3001';

// Oturum-doldu yönlendirmesi TEK sefer (401 storm → tek redirect). Sayfa navige olunca sıfırlanır.
let session401Handled = false;

// Kullanıcı BİLEREK logout yapınca: in-flight authenticated çağrılar (dashboard SWR poll vb.)
// token silindiği için 401 döner → interceptor "oturum süresi doldu" YANLIŞ-POZİTİFİ gösterip
// login'e ışınlar ve logout'un landing yönlendirmesini ezer (race → "bazen login bazen landing").
// Fix: logout, 401-redirect'i geçici bastırır. Kısa pencere → yeni oturumda gerçek session_expired
// yeniden çalışır (kalıcı susturma değil). authState.logout() başında çağrılır.
export function markLogoutInProgress(): void {
  session401Handled = true;
  setTimeout(() => { session401Handled = false; }, 4000);
}

// Axios instance oluştur
const axiosInstance = axios.create({
  baseURL: `${API_URL}/api`,
  timeout: 600000, // 60000'den 600000'e (10 dakika) yükseltildi
  headers: {
    'Content-Type': 'application/json'
  }
});

// GET bağlantı hatasında bir kez daha (TR erişim kesintisi, 22 Eki) — bkz baglantiYenidenDene
baglantiYenidenDeneKur(axiosInstance);

// Request interceptor - her istekte token ekle
axiosInstance.interceptors.request.use(
  async (config) => {
    try {
      // Supabase session'dan token al
      const { data: { session }, error } = await supabase.auth.getSession();

      if (error) {
        throw error;
      }

      if (session?.access_token) {
        config.headers.Authorization = `Bearer ${session.access_token}`;
      }
    } catch {
      // Token alınamadı - istek auth olmadan devam eder
    }
    // FormData → axios'un boundary auto-set'ini bozmamak için Content-Type'ı kaldır
    // (Saha Gözlem foto upload'ı bug debug raporu — global default 'application/json'
    // multipart upload'ı silent bozabiliyor.)
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      if (config.headers) {
        delete config.headers['Content-Type'];
        delete config.headers['content-type'];
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - hata yönetimi
axiosInstance.interceptors.response.use(
  (response) => {
    // Breadcrumb: başarılı API çağrısı izi (hata öncesi aksiyon trail'i için).
    addBreadcrumb('http', `${(response.config?.method || '').toUpperCase()} ${response.config?.url || ''} → ${response.status}`);
    return response;
  },
  async (error) => {
    // Breadcrumb: hatalı API çağrısı izi.
    addBreadcrumb('http.error', `${(error?.config?.method || '').toUpperCase()} ${error?.config?.url || ''} → ${error?.response?.status || error?.code || 'ERR'}`);
    // Backend maintenance mode aktifse (admin olmayan) → signOut + landing
    if (error?.response?.status === 503 && error.response?.data?.code === 'MAINTENANCE_MODE') {
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/auth/')) {
        try { await supabase.auth.signOut(); } catch { /* sessiz */ }
        window.location.href = '/';
      }
    }

    // 401 — token geçersiz / oturum süresi doldu. Authenticated app isteği 401 alıp Supabase de
    // GERÇEKTEN oturumsuzsa (yanlış-pozitif logout'u önlemek için ŞART), kullanıcıyı ölü sayfada
    // generic-hata ile bırakma → TEK sefer temiz login'e al + "oturum sona erdi" mesajı (?error=
    // session_expired) + ?next ile geri dönebilsin. public/auth/error-logs hariç. (useAuthBootstrap
    // SIGNED_OUT'u zaten yakalar; bu, in-flight 401'de kullanıcıya AÇIK sebep gösteren safety-net.)
    if (error?.response?.status === 401 && typeof window !== 'undefined' && !session401Handled) {
      const _url401 = error?.config?.url || '';
      // Anonim-erişilebilir sayfada (landing/blog/örnekler/araçlar/auth/bootstrapping) 401 →
      // login'e ZORLAMA. Oturumsuz ziyaretçi bu sayfalarda meşru; authed bir çağrı (örn. bir
      // optionalAuth endpoint'i) 401 dönse bile session_expired redirect'i public UX/SEO'yu kırar.
      // Canlı incident (2026-08-25): /kullanim/oturum ping'i her anonim landing'de 401 →
      // tüm anonim trafik login'e sekiyordu. isAnonAccessibleRoute (routePolicy SSoT) = onAuthPage üst-kümesi.
      const anonOk = isAnonAccessibleRoute(window.location.pathname);
      const skip401 = anonOk || _url401.includes('error-logs') || _url401.includes('/public/');
      if (!skip401) {
        try {
          const { data: { session: liveSession } } = await supabase.auth.getSession();
          if (!liveSession) {
            session401Handled = true;
            const next = encodeURIComponent(window.location.pathname + window.location.search);
            try { await supabase.auth.signOut(); } catch { /* sessiz */ }
            window.location.assign(`/auth/login?error=session_expired&next=${next}`);
          }
        } catch {
          /* getSession patladı — state machine SIGNED_OUT ile yönetsin, burada zorlama */
        }
      }
    }

    // 403 TIER_UPGRADE_REQUIRED — FREE user kilitli modüle URL ile direkt geldiğinde
    // global toast + uyelik sayfasına yönlendir (sayfa-level handler her modülde yok).
    // react-hot-toast'ta onClick seçeneği yok (eskiden verilen onClick sessizce yok sayılıyordu, tık hiçbir yere
    // gitmiyordu) → bölüm sayfasındaki "Geri Al" bildirimiyle aynı kalıp: mesaj + düğme.
    if (error?.response?.status === 403 && error.response?.data?.code === 'TIER_UPGRADE_REQUIRED') {
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/profil/uyelik')) {
        import('react-hot-toast').then(({ default: toast }) => {
          const data = error.response.data;
          const message = data.message || 'Bu özellik üst pakete özel';
          toast.error(
            (t) => createElement('div', { className: 'flex items-center gap-3' },
              createElement('span', { className: 'text-sm text-slate-700' }, message),
              createElement('button', {
                type: 'button',
                onClick: () => {
                  toast.dismiss(t.id);
                  window.location.href = '/profil/uyelik';
                },
                className: 'px-2.5 py-1 text-xs font-semibold text-slate-900 bg-white border border-slate-300 rounded-md hover:bg-slate-50 transition',
              }, 'Üyelik Yükselt')
            ),
            {
              duration: 6000,
              id: 'tier-upgrade-required',
              ariaProps: { role: 'alert', 'aria-live': 'assertive' },
            }
          );
        }).catch(() => {});
      }
    }

    // 402 Yetersiz Kredi — race condition fallback (AIConfirmDialog pre-check yetersiz görmedi
    // ama backend yetersiz dönerse). Merkezi KrediYukseltModal'ı tetiklemek için CustomEvent
    // dispatch. Toast YOK — modal SSoT (duplicate mesaj yasak).
    //
    // Listener: UserLayout.js → 'riskmatik:kredi-yetersiz' event → KrediYukseltModal aç.
    if (error?.response?.status === 402 && error.response?.data?.reason === 'INSUFFICIENT_KREDI') {
      if (typeof window !== 'undefined') {
        const data = error.response.data;
        window.dispatchEvent(new CustomEvent('riskmatik:kredi-yetersiz', {
          detail: {
            required: data.required ?? null,
            bakiye: data.bakiye ?? null,
            serviceKey: data.serviceKey ?? null,
            message: data.message ?? null,
          }
        }));
      }
    }

    // Sunucu (5xx) + ağ/timeout hatalarını KENDİ ErrorLog sistemimize raporla —
    // kullanıcı bildirmeden proaktif teşhis (Ali 2026-06-05). Fire-and-forget;
    // caller contract'ı DEĞİŞMEZ (yine reject). Beklenen iş-akışı 4xx
    // (401/402/403/404/409/422/429) + iptal edilen istekler raporlanmaz (gürültü).
    // error-logs POST'u ayrı plain-axios → interceptor loop yok.
    try {
      const _st = error?.response?.status;
      const _url = error?.config?.url || '';
      const _canceled = error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError';
      if (!_canceled && !_url.includes('error-logs')) {
        if (_st >= 500) {
          logAPIError(error, _url);
        } else if (!error.response && (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED')) {
          logNetworkError(error);
        }
      }
    } catch {
      /* sessiz — observability ASLA API akışını bozmaz */
    }
    return Promise.reject(error);
  }
);

export default axiosInstance;